import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  type CreateStrategyInput,
  type StrategyDto,
  type StrategySettings,
  type UpdateStrategyInput,
  withStrategyDefaults,
} from '@ultrasnip/contracts';
import { TrackedKeys } from '@/features/tracked/tracked-api';
import { api } from '@/lib/api-client';
import { QueryKeys } from '@/lib/queries';

export const StrategyKeys = {
  /** Persisted to IndexedDB (instant warm start). */
  list: ['strategies'] as const,
  defaults: ['strategies', 'defaults'] as const,
};

/** The user's strategies; the API creates a "Default" one on the first call. */
export const strategiesQuery = queryOptions({
  queryKey: StrategyKeys.list,
  queryFn: ({ signal }) => api.get<StrategyDto[]>('/strategies', { signal }),
  // Settings stored (or cached in IndexedDB) before a newer field existed get its default here, so
  // the editor always has every field, like the engine's `from_stored`.
  select: (list) =>
    list.map((strategy) => ({ ...strategy, settings: withStrategyDefaults(strategy.settings) })),
  staleTime: 30_000,
});

/** System defaults used by "Reset to defaults". */
export const strategyDefaultsQuery = queryOptions({
  queryKey: StrategyKeys.defaults,
  queryFn: ({ signal }) => api.get<StrategySettings>('/strategies/defaults', { signal }),
  select: withStrategyDefaults,
  staleTime: 5 * 60_000,
});

export function useStrategyMutations() {
  const queryClient = useQueryClient();
  const replaceOne = (updated: StrategyDto) =>
    queryClient.setQueryData<StrategyDto[]>(StrategyKeys.list, (list) =>
      list?.map((s) => (s.id === updated.id ? updated : s)),
    );
  // The default strategy is also mirrored on the profile; tracked wallets may point at a strategy.
  const refreshDependents = () => {
    void queryClient.invalidateQueries({ queryKey: QueryKeys.me });
    void queryClient.invalidateQueries({ queryKey: TrackedKeys.list });
  };

  const create = useMutation({
    mutationFn: (input: CreateStrategyInput) => api.post<StrategyDto>('/strategies', input),
    onSuccess: (created) =>
      queryClient.setQueryData<StrategyDto[]>(StrategyKeys.list, (list) => [...(list ?? []), created]),
  });
  const update = useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateStrategyInput }) =>
      api.patch<StrategyDto>(`/strategies/${id}`, input),
    onSuccess: replaceOne,
  });
  const setDefault = useMutation({
    mutationFn: (id: string) => api.post<StrategyDto[]>(`/strategies/${id}/default`),
    onSuccess: (list) => {
      queryClient.setQueryData(StrategyKeys.list, list);
      refreshDependents();
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete<void>(`/strategies/${id}`),
    onSuccess: (_result, id) => {
      queryClient.setQueryData<StrategyDto[]>(StrategyKeys.list, (list) => list?.filter((s) => s.id !== id));
      refreshDependents();
    },
  });
  return { create, update, setDefault, remove };
}
